import { useEffect, useState } from 'react';
import { canUseDOM } from '@marriott/mi-ui-library';

/**
 * useGlobalNavVisibility
 *
 * Tracks whether the sticky global navigation should be visible based on the
 * user's scroll direction. The nav is shown at the top of the page and when
 * scrolling up, and hidden while scrolling down. Safe to call during SSR — the
 * scroll listener is attached only client-side (guarded by canUseDOM).
 *
 * @param enabled - When false, the hook is inert and always returns false.
 * @returns Whether the global nav should currently be visible.
 */
export const useGlobalNavVisibility = (enabled = false): boolean => {
  const [isNavVisible, setIsNavVisible] = useState(true);

  useEffect(() => {
    if (!enabled || !canUseDOM) {
      return;
    }
    setIsNavVisible(window.scrollY <= 0);
    let lastScrollY = window.scrollY;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY <= 0) {
        setIsNavVisible(true);
      } else if (currentY > lastScrollY) {
        setIsNavVisible(false);
      } else if (currentY < lastScrollY) {
        setIsNavVisible(true);
      }
      lastScrollY = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enabled]);
  return enabled && isNavVisible;
};
